import { Component, OnDestroy, OnInit } from "@angular/core";
import { IWidgetParameter, PreferenceContext } from "@comunicare/comunicare-interfaces";
import { CookieService } from "ngx-cookie-service";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { first, map, switchMap, takeUntil } from "rxjs/operators";
import { WidgetItem } from "src/app/components/widgets/widget-item";
import { ModalManagementService } from "src/app/providers/modal-management.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { WidgetManagerService } from "src/app/providers/widget-manager.service";
import { LoginPageComponent } from "../login-page/login-page.component";

@Component({
  selector: "app-dashboard-page",
  templateUrl: "./dashboard-page.component.html",
  styleUrls: ["./dashboard-page.component.scss"],
})
export class DashboardPageComponent implements OnDestroy, OnInit {
  public widgetParameter: IWidgetParameter;
  public widgetItems: WidgetItem[];
  public nbColumn = 3;
  private isWidgetMoved = false;

  /** Subject that emits when the component has been destroyed. */
  private onDestroy$ = new Subject<void>();

  constructor(
    private widgetManagerService: WidgetManagerService,
    private preferenceService: PreferenceService,
    private sessionService: SessionService,
    private cookieService: CookieService,
    private modalManagementService: ModalManagementService,
    private userService: UserService
  ) {
    this.loadPreference();
  }

  ngOnInit(): void {
    this.sessionService.refreshDashboardPage.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.loadPreference();
      // avoid bug when dragging tile after updating preference; see CMATE-3694 + CMATE-4181
      if (this.isWidgetMoved) {
        window.location.reload();
      }
    });

    if (this.cookieService.check(LoginPageComponent.COOKIE_REDIRECT_MODALID)) {
      const modalId = this.cookieService.get(LoginPageComponent.COOKIE_REDIRECT_MODALID);
      const source = this.cookieService.get(LoginPageComponent.COOKIE_REDIRECT_SOURCE);
      this.cookieService.delete(LoginPageComponent.COOKIE_REDIRECT_MODALID);
      this.cookieService.delete(LoginPageComponent.COOKIE_REDIRECT_SOURCE);
      this.modalManagementService.redirectToGivenModal(modalId, source);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public widgetMoved(widgets: WidgetItem[]): void {
    this.isWidgetMoved = true;
    this.preferenceService
      .update({
        context: PreferenceContext.WIDGET,
        parameters: { ...this.widgetParameter, widgets },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private loadPreference() {
    this.preferenceService
      .list(PreferenceContext.WIDGET)
      .pipe(
        takeUntil(this.onDestroy$),
        first(),
        switchMap((widgetParameter: IWidgetParameter) => {
          this.widgetParameter = widgetParameter ?? this.widgetManagerService.defaultParameter;
          this.nbColumn = this.widgetParameter.nbColumn;
          const widgetItems = this.widgetManagerService.getWidgetItems(this.widgetParameter.widgets);
          return this.filterAuthorizedWidgets$(widgetItems);
        })
      )
      .subscribe((filteredWidgets) => {
        this.widgetItems = filteredWidgets;
      });
  }

  private filterAuthorizedWidgets$(widgetItems: WidgetItem[]): Observable<WidgetItem[]> {
    const authChecks = widgetItems.map((widget) => ({
      widget,
      auth$: this.widgetManagerService.widgetAuthorizationMap[widget.name]
        ? this.userService.isAuthorized(this.widgetManagerService.widgetAuthorizationMap[widget.name], "GET").pipe(first())
        : of(true), // If no restriction, consider as authorized
    }));

    return forkJoin(authChecks.map(({ auth$ }) => auth$)).pipe(
      map((authResults) =>
        authChecks
          .map(({ widget }, index) => ({ widget, authorized: authResults[index] }))
          .filter(({ authorized }) => authorized)
          .map(({ widget }) => widget)
      )
    );
  }
}
